/**
 * Two-factor authentication: enrolling a device, and the tenant's policy.
 *
 * `MfaEnrol` is used in two places — Settings › Security, and the tenant picker
 * when a tenant requires MFA of someone who has none. Before it was shared the
 * picker told that person to "enter a tenant that does not require it and set
 * up an authenticator", which a person with one tenant cannot do: a dead end,
 * and the reason MFA could not default on (PLAN-2026-09-15 §3B item 5).
 */

import { QRCodeSVG } from 'qrcode.react';
import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from './api.ts';
import { Banner, Button, Field, Loading, Section } from './components/index.ts';

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));

export function MfaEnrol({
  enrolled,
  onEnrolled,
  continueLabel,
}: {
  enrolled: boolean;
  /** Called once the recovery codes have been shown and the person moves on. */
  onEnrolled: () => void;
  /** When set, a button under the recovery codes that calls `onEnrolled`. */
  continueLabel?: string;
}): React.JSX.Element {
  const [enrolment, setEnrolment] = useState<{ secretBase32: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <>
      {codes === null &&
        (enrolment === null ? (
          <Button
            variant="primary"
            busy={busy}
            onClick={async () => {
              setError(null);
              setBusy(true);
              try {
                setEnrolment(await api.post<{ secretBase32: string; otpauthUri: string }>('/api/auth/mfa/enrol'));
              } catch (err) {
                setError(failure(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            {enrolled ? 'Re-enrol a device' : 'Set up an authenticator'}
          </Button>
        ) : (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              setBusy(true);
              try {
                const result = await api.post<{ recoveryCodes: string[] }>('/api/auth/mfa/confirm', { code });
                setCodes(result.recoveryCodes);
                setEnrolment(null);
                setCode('');
                if (continueLabel === undefined) onEnrolled();
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'could not confirm');
              } finally {
                setBusy(false);
              }
            }}
          >
            <p className="small muted">
              Add this to your authenticator, then enter the code it shows. Enrolment is not active until a
              code is verified.
            </p>
            {/*
              * Fixed black-on-white, and NOT the theme's colours: scanners want
              * dark modules on a light field, in whatever light the site has.
              * Bundled rather than fetched, because this console is behind a VPN.
              */}
            <div className="qr">
              <QRCodeSVG value={enrolment.otpauthUri} size={168} level="M" marginSize={0} />
            </div>
            <p className="small muted">Or enter this key by hand if your authenticator cannot scan:</p>
            <p className="mono small wrap-any">{enrolment.secretBase32}</p>
            <Field id="mfa-code" label="Code from your authenticator">
              <input
                id="mfa-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            <Button type="submit" variant="primary" busy={busy} disabled={code.trim() === ''}>
              Confirm
            </Button>
          </form>
        ))}

      {error !== null && <Banner tone="fault">{error}</Banner>}

      {codes !== null && (
        <div className="stack">
          <h3>Recovery codes</h3>
          <p className="small muted">
            Shown once. They are stored only as hashes and cannot be retrieved — if you lose these and your
            device, an administrator has to re-enrol you.
          </p>
          <ul className="mono small">
            {codes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <div className="row">
            <Button
              onClick={() => {
                void navigator.clipboard
                  .writeText(codes.join('\n'))
                  .then(() => setCopied(true))
                  // Clipboard access can be refused. Say so rather than "Copied"
                  // over a clipboard that did not change.
                  .catch(() => setError('could not copy — the codes are listed above'));
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <a
              className="small"
              download="cosmon-recovery-codes.txt"
              href={`data:text/plain;charset=utf-8,${encodeURIComponent(codes.join('\n'))}`}
            >
              Download
            </a>
            {continueLabel !== undefined && (
              <Button variant="primary" onClick={onEnrolled}>
                {continueLabel}
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

interface TenantSecurity {
  requireMfa: boolean;
  members: number;
  membersWithoutMfa: number;
  canChange: boolean;
  callerHasMfa: boolean;
}

/**
 * Whether this tenant requires MFA of everyone who enters it.
 *
 * Owners and admins change it. Turning it on is refused to someone without a
 * satisfied second factor themselves, and it ends the tenant sessions of
 * members who have not satisfied one, so the rule applies now rather than at
 * their next sign-in.
 */
export function TenantMfaPolicy(): React.JSX.Element | null {
  const [state, setState] = useState<TenantSecurity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await api.get<TenantSecurity>('/api/tenant/security'));
    } catch (err) {
      setError(failure(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null && state === null) return <Banner tone="fault">{error}</Banner>;
  if (state === null) return <Loading rows={2} cols={1} label="Loading the tenant's MFA policy" />;

  return (
    <Section
      title="Require two-factor authentication"
      summary={
        state.requireMfa
          ? `Required for everyone who enters this tenant. ${state.membersWithoutMfa} of ${state.members} members have not set one up; they are asked to the next time they enter.`
          : `Optional. ${state.membersWithoutMfa} of ${state.members} members have no second factor.`
      }
    >
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {done !== null && <Banner tone="ok">{done}</Banner>}
        {!state.canChange ? (
          <p className="small muted">Owners and administrators can change this.</p>
        ) : state.requireMfa ? (
          <Button
            variant="danger"
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.put('/api/tenant/security', { requireMfa: false });
                setDone('Two-factor authentication is now optional in this tenant.');
                await load();
              } catch (err) {
                setError(failure(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            Make it optional
          </Button>
        ) : (
          <>
            {!state.callerHasMfa && (
              <Banner tone="warn">Set up your own authenticator above first.</Banner>
            )}
            <Button
              variant="primary"
              busy={busy}
              disabled={!state.callerHasMfa}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  const res = await api.put<{ sessionsEnded: number }>('/api/tenant/security', { requireMfa: true });
                  setDone(
                    `Required from now on. ${res.sessionsEnded} session(s) without a second factor left this tenant and will be asked for one.`,
                  );
                  await load();
                } catch (err) {
                  setError(failure(err));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Require it for everyone
            </Button>
          </>
        )}
      </div>
    </Section>
  );
}
